import { useState, useMemo, useEffect } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { MultiSelect } from "../ui/multi-select";
import { Select } from "../ui/select";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import {
    Download,
    AlertCircle,
    CheckCircle,
    Filter
} from "lucide-react";
import { toast } from "sonner";
import { bulkUpdateLeads, fetchDistinctValues } from "../../services/crmServices/leadService";
import { getFullName } from "../../services/common/util";

export function DuplicateReviewPanel({
    duplicateLeads,
    leadStatuses,
    availableAgents,
    availableFloorManagers,
    availableGeneralManagers,
    availableDesks,
    importing,
    setImporting,
    onSuccess
}) {
    const [showDuplicateReview, setShowDuplicateReview] = useState(false);
    const [selectedOriginalLeads, setSelectedOriginalLeads] = useState([]);
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [bulkStatus, setBulkStatus] = useState("");
    const [bulkSource, setBulkSource] = useState("");
    const [bulkAssignmentType, setBulkAssignmentType] = useState("");
    const [bulkAssignmentValue, setBulkAssignmentValue] = useState("");

    const [statusFilter, setStatusFilter] = useState([]);
    const [sourceFilter, setSourceFilter] = useState([]);
    const [filterInitialized, setFilterInitialized] = useState(false);
    const [crmSources, setCrmSources] = useState([]);

    useEffect(() => {
        if (duplicateLeads.length > 0 && !showDuplicateReview) {
            setShowDuplicateReview(true);
        }
    }, [duplicateLeads.length]);

    useEffect(() => {
        if (duplicateLeads.length > 0 && !filterInitialized && leadStatuses.length > 0) {
            setStatusFilter(leadStatuses.map(s => s.value));
            setFilterInitialized(true);
        }
    }, [duplicateLeads.length, leadStatuses, filterInitialized]);

    useEffect(() => {
        if (duplicateLeads.length > 0) {
            fetchDistinctValues("source").then((res) => {
                if (res?.data) {
                    setCrmSources(res.data.map(s => ({ label: s, value: s })));
                }
            });
        }
    }, [duplicateLeads.length]);

    const handleToggleReview = () => {
        if (!showDuplicateReview && !filterInitialized) {
            setStatusFilter(leadStatuses.map(s => s.value));
            setFilterInitialized(true);
        }
        setShowDuplicateReview(!showDuplicateReview);
    };

    const allOriginalLeads = useMemo(() => {
        const originals = [];
        duplicateLeads.forEach(dup => {
            if (dup.originalLeads?.length > 0) {
                originals.push(...dup.originalLeads);
            }
        });
        return originals;
    }, [duplicateLeads]);

    // Extract unique sources from original leads for the source filter
    const availableSources = useMemo(() => {
        const sources = new Set();
        allOriginalLeads.forEach(lead => {
            if (lead.source) sources.add(lead.source);
        });
        return Array.from(sources).sort().map(s => ({ label: s, value: s }));
    }, [allOriginalLeads]);

    useEffect(() => {
        if (filterInitialized && sourceFilter.length === 0 && availableSources.length > 0) {
            setSourceFilter(availableSources.map(s => s.value));
        }
    }, [availableSources, filterInitialized]);

    const filteredDuplicates = useMemo(() => {
        if (statusFilter.length === 0) return [];

        const normalizedStatusFilter = statusFilter.map(s => s?.toString().trim().toLowerCase()).filter(Boolean);
        const normalizedSourceFilter = sourceFilter.map(s => s?.toString().trim().toLowerCase()).filter(Boolean);
        const allSourcesSelected = sourceFilter.length === availableSources.length;

        return duplicateLeads
            .map(dup => {
                if (!dup.originalLeads?.length) return null;
                const matchingOriginals = dup.originalLeads.filter(orig => {
                    const leadStatus = (orig.status || "").toString().trim().toLowerCase();
                    if (!normalizedStatusFilter.includes(leadStatus)) return false;

                    if (!allSourcesSelected && normalizedSourceFilter.length > 0) {
                        const leadSource = (orig.source || "").toString().trim().toLowerCase();
                        if (!normalizedSourceFilter.includes(leadSource)) return false;
                    }

                    return true;
                });
                if (matchingOriginals.length === 0) return null;
                return { ...dup, originalLeads: matchingOriginals };
            })
            .filter(Boolean);
    }, [duplicateLeads, statusFilter, sourceFilter, availableSources, leadStatuses]);

    const filteredOriginalCount = useMemo(() => {
        let count = 0;
        filteredDuplicates.forEach(dup => {
            count += dup.originalLeads?.length || 0;
        });
        return count;
    }, [filteredDuplicates]);

    const handleStatusFilterChange = (selected) => {
        setStatusFilter(selected);
        setSelectedOriginalLeads([]);
    };

    const handleSelectAllStatuses = () => {
        setStatusFilter(leadStatuses.map(s => s.value));
    };

    const handleDeselectAllStatuses = () => {
        setStatusFilter([]);
    };

    const handleSourceFilterChange = (selected) => {
        setSourceFilter(selected);
        setSelectedOriginalLeads([]);
    };

    const handleSelectAllSources = () => {
        setSourceFilter(availableSources.map(s => s.value));
    };

    const handleDeselectAllSources = () => {
        setSourceFilter([]);
    };

    const handleToggleOriginalLead = (leadId) => {
        setSelectedOriginalLeads(prev => {
            const isSelected = prev.some(l => l._id === leadId);
            if (isSelected) {
                return prev.filter(l => l._id !== leadId);
            }
            let fullLead = null;
            filteredDuplicates.forEach(dup => {
                if (dup.originalLeads) {
                    const found = dup.originalLeads.find(orig => orig._id === leadId);
                    if (found) fullLead = found;
                }
            });
            return fullLead ? [...prev, fullLead] : prev;
        });
    };

    const handleSelectAllOriginalLeads = () => {
        const allVisible = [];
        filteredDuplicates.forEach(dup => {
            if (dup.originalLeads?.length > 0) {
                allVisible.push(...dup.originalLeads);
            }
        });
        const unique = [];
        const seen = new Set();
        allVisible.forEach(lead => {
            if (!seen.has(lead._id)) {
                seen.add(lead._id);
                unique.push(lead);
            }
        });
        setSelectedOriginalLeads(unique);
        toast.success(`Selected ${unique.length} original leads`);
    };

    const handleClearSelection = () => {
        setSelectedOriginalLeads([]);
        setBulkStatus("");
        setBulkSource("");
        setBulkAssignmentType("");
        setBulkAssignmentValue("");
    };

    const handleApplyBulkActions = async () => {
        if (selectedOriginalLeads.length === 0) {
            toast.error("Please select leads to update");
            return;
        }

        const leadIds = selectedOriginalLeads.map(l => l._id);
        const updates = {};

        if (bulkStatus) {
            updates.status = bulkStatus;
        }

        if (bulkSource.trim()) {
            updates.source = bulkSource.trim();
        }

        if (bulkAssignmentType && bulkAssignmentValue) {
            if (bulkAssignmentType === "generalManager") {
                updates.generalManager = bulkAssignmentValue;
                updates.floorManager = null;
                updates.desk = null;
                updates.agent = null;
            } else if (bulkAssignmentType === "floorManager") {
                updates.floorManager = bulkAssignmentValue;
                updates.desk = null;
                updates.agent = null;
            } else if (bulkAssignmentType === "desk") {
                updates.desk = bulkAssignmentValue;
                updates.agent = null;
            } else if (bulkAssignmentType === "agent") {
                updates.agent = bulkAssignmentValue;
            }
        }

        if (Object.keys(updates).length === 0) {
            toast.error("Please select at least one action");
            return;
        }

        try {
            setImporting(true);
            const response = await bulkUpdateLeads({ leadIds, updates });

            if (response?.status === 200) {
                toast.success(`Successfully updated ${leadIds.length} lead(s)`);
                handleClearSelection();
                setShowBulkActions(false);
                if (onSuccess) onSuccess();
            } else {
                toast.error(response?.data?.message || "Failed to update leads");
            }
        } catch (error) {
            console.error("Error updating leads:", error);
            toast.error("Failed to update leads");
        } finally {
            setImporting(false);
        }
    };

    const downloadDuplicates = () => {
        if (duplicateLeads.length === 0) {
            toast.error("No duplicate leads to download");
            return;
        }

        const headers = [
            "Type", "First Name", "Last Name", "Email", "Phone", "Second Phone", "Country", "Status", "Source", "Extra",
            "Duplicate Reason", "Duplicate Phone(s)", "Original Lead ID", "Original Created At"
        ];

        const escapeCsvValue = (value) => {
            if (value === null || value === undefined) return "";
            const stringValue = String(value);
            if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
        };

        const rows = [];
        duplicateLeads.forEach(duplicate => {
            const duplicatePhonesStr = duplicate.duplicatePhones ? duplicate.duplicatePhones.join("; ") : "";

            rows.push([
                "DUPLICATE",
                escapeCsvValue(duplicate.firstName || ""),
                escapeCsvValue(duplicate.lastName || ""),
                escapeCsvValue(duplicate.email || ""),
                escapeCsvValue(duplicate.phone || ""),
                escapeCsvValue(duplicate.secondPhone || ""),
                escapeCsvValue(duplicate.country || ""),
                escapeCsvValue(duplicate.status || ""),
                escapeCsvValue(duplicate.source || ""),
                escapeCsvValue(duplicate.extra || ""),
                escapeCsvValue(duplicate.reason || ""),
                escapeCsvValue(duplicatePhonesStr),
                "",
                ""
            ].join(","));

            if (duplicate.originalLeads?.length > 0) {
                duplicate.originalLeads.forEach(original => {
                    rows.push([
                        "ORIGINAL",
                        escapeCsvValue(original.firstName || ""),
                        escapeCsvValue(original.lastName || ""),
                        escapeCsvValue(original.email || ""),
                        escapeCsvValue(original.phone || ""),
                        escapeCsvValue(original.secondPhone || ""),
                        escapeCsvValue(original.country || ""),
                        escapeCsvValue(original.status || ""),
                        escapeCsvValue(original.source || ""),
                        "",
                        "",
                        "",
                        escapeCsvValue(original._id || ""),
                        escapeCsvValue(original.createdAt ? new Date(original.createdAt).toISOString() : "")
                    ].join(","));
                });
            }
        });

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
        link.download = `duplicate_leads_with_originals_${timestamp}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);

        toast.success(`Downloaded ${duplicateLeads.length} duplicate leads with originals`);
    };

    if (duplicateLeads.length === 0) return null;

    return (
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                    <div>
                        <h4 className="font-medium text-yellow-900">
                            {duplicateLeads.length} Duplicate Lead(s) Found
                        </h4>
                        <p className="text-sm text-yellow-700">
                            These leads were skipped because they already exist in the system.
                        </p>
                    </div>
                </div>
                <div className="flex space-x-2">
                    <Button
                        variant="outline"
                        onClick={handleToggleReview}
                        className="text-yellow-700 border-yellow-600 hover:bg-yellow-100"
                    >
                        {showDuplicateReview ? "Hide Review" : "Review & Manage"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={downloadDuplicates}
                        className="text-yellow-700 border-yellow-600 hover:bg-yellow-100"
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                    </Button>
                </div>
            </div>

            {showDuplicateReview && (
                <div className="mt-4 space-y-4">
                    {/* Filters */}
                    <Card className="border-indigo-200">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center">
                                <Filter className="h-4 w-4 mr-2" />
                                Filters
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Status Filter */}
                            <div className="space-y-2">
                                <Label className="text-xs font-medium">By Status</Label>
                                <MultiSelect
                                    options={leadStatuses.map(s => ({ label: s.label, value: s.value }))}
                                    selected={statusFilter}
                                    onChange={handleStatusFilterChange}
                                    placeholder="Select statuses to show..."
                                    searchPlaceholder="Search statuses..."
                                />
                                <div className="flex flex-wrap gap-2">
                                    <Button size="sm" variant="outline" onClick={handleSelectAllStatuses} className="text-xs h-7">
                                        Select All
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={handleDeselectAllStatuses} className="text-xs h-7">
                                        Deselect All
                                    </Button>
                                </div>
                            </div>

                            {/* Source Filter */}
                            {availableSources.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-xs font-medium">By Source</Label>
                                    <MultiSelect
                                        options={availableSources}
                                        selected={sourceFilter}
                                        onChange={handleSourceFilterChange}
                                        placeholder="Select sources to show..."
                                        searchPlaceholder="Search sources..."
                                    />
                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" variant="outline" onClick={handleSelectAllSources} className="text-xs h-7">
                                            Select All
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={handleDeselectAllSources} className="text-xs h-7">
                                            Deselect All
                                        </Button>
                                    </div>
                                </div>
                            )}

                            <p className="text-xs text-gray-500">
                                Showing {filteredDuplicates.length} of {duplicateLeads.length} duplicate groups
                                ({filteredOriginalCount.toLocaleString()} of {allOriginalLeads.length.toLocaleString()} originals match)
                            </p>
                        </CardContent>
                    </Card>

                    {/* Select All / Selection Controls */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={handleSelectAllOriginalLeads}
                                disabled={filteredOriginalCount === 0}
                                className="text-xs"
                            >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Select All Filtered ({filteredOriginalCount.toLocaleString()})
                            </Button>
                            {selectedOriginalLeads.length > 0 && (
                                <>
                                    <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                                        {selectedOriginalLeads.length.toLocaleString()} selected
                                    </Badge>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={handleClearSelection}
                                        className="text-xs text-gray-600"
                                    >
                                        Clear
                                    </Button>
                                </>
                            )}
                        </div>
                        {selectedOriginalLeads.length > 0 && (
                            <Button
                                size="sm"
                                onClick={() => setShowBulkActions(!showBulkActions)}
                            >
                                {showBulkActions ? "Hide" : "Show"} Bulk Actions
                            </Button>
                        )}
                    </div>

                    {/* Bulk Actions Panel */}
                    {showBulkActions && selectedOriginalLeads.length > 0 && (
                        <Card className="border-blue-200">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm">Bulk Actions for {selectedOriginalLeads.length.toLocaleString()} Lead(s)</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                        <Label className="text-xs">Change Status</Label>
                                        <Select
                                            options={leadStatuses}
                                            value={bulkStatus}
                                            onChange={setBulkStatus}
                                            placeholder="Select new status"
                                            searchable
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Change Source</Label>
                                        <Input
                                            value={bulkSource}
                                            onChange={(e) => setBulkSource(e.target.value)}
                                            placeholder="Select or type new source"
                                            list="bulk-source-options"
                                        />
                                        {crmSources.length > 0 && (
                                            <datalist id="bulk-source-options">
                                                {crmSources.map((source) => (
                                                    <option key={source.value} value={source.value} />
                                                ))}
                                            </datalist>
                                        )}
                                    </div>
                                    <div>
                                        <Label className="text-xs">Assignment Type</Label>
                                        <Select
                                            options={[
                                                { label: "Assign to General Manager", value: "generalManager" },
                                                { label: "Assign to Floor Manager", value: "floorManager" },
                                                { label: "Assign to Desk", value: "desk" },
                                                { label: "Assign to Agent", value: "agent" }
                                            ]}
                                            value={bulkAssignmentType}
                                            onChange={(value) => {
                                                setBulkAssignmentType(value);
                                                setBulkAssignmentValue("");
                                            }}
                                            placeholder="Select assignment type"
                                        />
                                    </div>
                                </div>

                                {bulkAssignmentType === "generalManager" && (
                                    <div>
                                        <Label className="text-xs">Select General Manager</Label>
                                        <Select
                                            options={availableGeneralManagers.map(gm => ({
                                                label: getFullName(gm),
                                                value: gm._id
                                            }))}
                                            value={bulkAssignmentValue}
                                            onChange={setBulkAssignmentValue}
                                            placeholder="Select general manager"
                                            searchable
                                        />
                                    </div>
                                )}

                                {bulkAssignmentType === "floorManager" && (
                                    <div>
                                        <Label className="text-xs">Select Floor Manager</Label>
                                        <Select
                                            options={availableFloorManagers.map(fm => ({
                                                label: getFullName(fm),
                                                value: fm._id
                                            }))}
                                            value={bulkAssignmentValue}
                                            onChange={setBulkAssignmentValue}
                                            placeholder="Select floor manager"
                                            searchable
                                        />
                                    </div>
                                )}

                                {bulkAssignmentType === "desk" && (
                                    <div>
                                        <Label className="text-xs">Select Desk</Label>
                                        <Select
                                            options={availableDesks.map(desk => ({
                                                label: desk.name,
                                                value: desk._id
                                            }))}
                                            value={bulkAssignmentValue}
                                            onChange={setBulkAssignmentValue}
                                            placeholder="Select desk"
                                            searchable
                                        />
                                    </div>
                                )}

                                {bulkAssignmentType === "agent" && (
                                    <div>
                                        <Label className="text-xs">Select Agent</Label>
                                        <Select
                                            options={availableAgents.map(agent => ({
                                                label: getFullName(agent),
                                                value: agent._id
                                            }))}
                                            value={bulkAssignmentValue}
                                            onChange={setBulkAssignmentValue}
                                            placeholder="Select agent"
                                            searchable
                                        />
                                    </div>
                                )}

                                {selectedOriginalLeads.length > 0 && (bulkStatus || bulkSource.trim() || bulkAssignmentValue) && (
                                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-900">
                                        <p className="font-medium mb-1">Summary</p>
                                        <p>
                                            <strong>{selectedOriginalLeads.length} lead(s)</strong>
                                            {(() => {
                                                const statusCounts = {};
                                                selectedOriginalLeads.forEach(lead => {
                                                    const label = leadStatuses.find(s => s.value === lead.status)?.label || lead.status || "Unknown";
                                                    statusCounts[label] = (statusCounts[label] || 0) + 1;
                                                });
                                                const parts = Object.entries(statusCounts).map(([label, count]) => `${count} ${label}`);
                                                return parts.length > 0 ? ` (${parts.join(", ")})` : "";
                                            })()}
                                            {bulkAssignmentValue && bulkAssignmentType && (() => {
                                                let targetName = "";
                                                if (bulkAssignmentType === "agent") {
                                                    targetName = availableAgents.find(a => a._id === bulkAssignmentValue);
                                                    targetName = targetName ? getFullName(targetName) : bulkAssignmentValue;
                                                } else if (bulkAssignmentType === "desk") {
                                                    targetName = availableDesks.find(d => d._id === bulkAssignmentValue)?.name || bulkAssignmentValue;
                                                } else if (bulkAssignmentType === "floorManager") {
                                                    targetName = availableFloorManagers.find(f => f._id === bulkAssignmentValue);
                                                    targetName = targetName ? getFullName(targetName) : bulkAssignmentValue;
                                                } else if (bulkAssignmentType === "generalManager") {
                                                    targetName = availableGeneralManagers.find(g => g._id === bulkAssignmentValue);
                                                    targetName = targetName ? getFullName(targetName) : bulkAssignmentValue;
                                                }
                                                const typeLabel = bulkAssignmentType === "generalManager" ? "general manager" : bulkAssignmentType === "floorManager" ? "floor manager" : bulkAssignmentType;
                                                return ` → will be assigned to ${typeLabel}: ${targetName}`;
                                            })()}
                                            {bulkStatus && (() => {
                                                const label = leadStatuses.find(s => s.value === bulkStatus)?.label || bulkStatus;
                                                return ` → status changed to "${label}"`;
                                            })()}
                                            {bulkSource.trim() && ` → source changed to "${bulkSource.trim()}"`}
                                        </p>
                                    </div>
                                )}

                                <div className="flex space-x-2 pt-2">
                                    <Button
                                        onClick={handleApplyBulkActions}
                                        disabled={importing || (!bulkStatus && !bulkSource.trim() && !bulkAssignmentValue)}
                                    >
                                        {importing ? "Updating..." : "Apply to Selected"}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowBulkActions(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Duplicate Lead Cards */}
                    <div className="max-h-[500px] overflow-y-auto space-y-4">
                        {filteredDuplicates.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <Filter className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                <p className="text-sm">No duplicates match the selected status filter.</p>
                                <p className="text-xs mt-1">Try selecting more statuses above.</p>
                            </div>
                        ) : (
                            filteredDuplicates.map((duplicate, dupIndex) => (
                                <div key={dupIndex} className="border rounded-lg bg-white overflow-hidden">
                                    <div className="bg-red-50 border-b border-red-200 p-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <Badge variant="destructive" className="text-xs">DUPLICATE (Not Imported)</Badge>
                                                <span className="font-medium text-red-900">
                                                    {duplicate.firstName || ""} {duplicate.lastName || ""}
                                                </span>
                                                <span className="text-sm text-red-700">
                                                    {duplicate.phone || duplicate.secondPhone || "No phone"}
                                                </span>
                                            </div>
                                            <span className="text-xs text-red-600 italic">
                                                {duplicate.reason || "Duplicate found"}
                                            </span>
                                        </div>
                                    </div>

                                    {duplicate.originalLeads?.length > 0 ? (
                                        <div className="divide-y">
                                            {duplicate.originalLeads.map((original, origIndex) => (
                                                <div key={origIndex} className="bg-green-50 p-3">
                                                    <div className="flex items-center space-x-3 mb-2">
                                                        <Checkbox
                                                            checked={selectedOriginalLeads.some(l => l._id === original._id)}
                                                            onCheckedChange={() => handleToggleOriginalLead(original._id)}
                                                        />
                                                        <Badge className="bg-emerald-600 text-white text-xs">ORIGINAL</Badge>
                                                        <span className="font-medium text-green-900">
                                                            {original.firstName || ""} {original.lastName || ""}
                                                        </span>
                                                        <span className="text-sm text-green-700">
                                                            {original.phone || original.secondPhone || "No phone"}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-green-800">
                                                        <div>
                                                            <span className="font-medium">Email:</span> {original.email || "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Status:</span> {original.status || "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Agent:</span> {original.agent?.name || "Unassigned"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Desk:</span> {original.desk?.name || "Unassigned"}
                                                        </div>
                                                        {original.floorManager && (
                                                            <div>
                                                                <span className="font-medium">Floor Manager:</span> {original.floorManager.name}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <span className="font-medium">Country:</span> {original.country || "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Source:</span> {original.source || "-"}
                                                        </div>
                                                        <div>
                                                            <span className="font-medium">Created:</span> {original.createdAt ? new Date(original.createdAt).toLocaleDateString() : "-"}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-3 text-sm text-gray-500 italic">
                                            No matching original leads found
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
